import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, Card } from '@kss/ui';
import { Link } from 'react-router-dom';
import { apiResources, type ApiResourceMeta } from '@kss/api';
import { ArrowRight, Database, ExternalLink } from 'lucide-react';

/**
 * Universal "module-not-yet-built" route. Instead of showing the
 * static "Coming Soon" placeholder, we look up the URL slug in the
 * auto-generated `apiResources` registry and redirect to the
 * generic Browser/CRUD page that already exists for every API
 * resource.
 *
 * Usage:
 *   <Route path="sales-orders" element={<ModuleRedirect slug="sales-orders" />} />
 *   <Route path="*"            element={<ModuleRedirect />} />          // catch-all
 *
 * The slug → resource mapping uses three strategies:
 *   1. Explicit hand-curated SLUG_MAP (best for irregular pluralisation).
 *   2. Auto-match: kebab-case slug → PascalCase resource name.
 *   3. Plural strip: drop trailing "s" / "ies" and retry.
 *
 * If we still can't find a match, we render a helpful fallback UI
 * that suggests the closest resources rather than a dead-end page.
 */

/* -------------------------------------------------------------------- */
/*  Hand-curated slug → registry resource map                           */
/* -------------------------------------------------------------------- */

interface SlugTarget {
  group: string;
  resource: string;
}

/**
 * Direct slug → registry resource overrides. Keys MUST match the URL
 * segment after `/panel/`. Anything not listed falls back to the
 * automatic resolver below.
 */
const SLUG_MAP: Record<string, SlugTarget> = {
  // ============================================================ //
  //  Nested slugs the live `/api/Menu/all` payload returns. The   //
  //  panel resolver hits these directly (no segment walking).     //
  // ============================================================ //
  'assets/fixed':         { group: 'asset-management', resource: 'FixedAsset' },
  'assets/depreciation':  { group: 'asset-management', resource: 'DepreciationSchedule' },
  'sales/orders':         { group: 'sales',        resource: 'SalesOrder' },
  'sales/returns':        { group: 'sales',        resource: 'SalesReturn' },
  'sales/challan':        { group: 'sales',        resource: 'DeliveryChallan' },
  'sales/invoices':       { group: 'sales',        resource: 'SalesInvoice' },
  'sales/payments':       { group: 'sales',        resource: 'SalesPayment' },
  'sales/credit-notes':   { group: 'sales',        resource: 'CreditNote' },
  'sales/debit-notes':    { group: 'sales',        resource: 'DebitNote' },
  'pos/cash':             { group: 'restaurant',   resource: 'CashDrawerSession' },
  'pos/terminals':        { group: 'restaurant',   resource: 'PosTerminal' },
  'pos/shifts':           { group: 'restaurant',   resource: 'CashDrawerSession' },
  'restaurant/delivery':  { group: 'restaurant',   resource: 'HomeDelivery' },
  'restaurant/kot':       { group: 'restaurant',   resource: 'Kot' },
  'restaurant/recipes':   { group: 'restaurant',   resource: 'Recipe' },
  'restaurant/reservations': { group: 'restaurant', resource: 'TableReservation' },
  'crm/tickets':          { group: 'crm',          resource: 'SupportTicket' },
  'crm/leads':            { group: 'crm',          resource: 'Lead' },
  'crm/opportunities':    { group: 'crm',          resource: 'Opportunity' },
  'crm/quotations':       { group: 'crm',          resource: 'Quotation' },
  'crm/campaigns':        { group: 'crm',          resource: 'Campaign' },
  'crm/loyalty':          { group: 'crm',          resource: 'CustomerLoyaltyAccount' },
  'purchase/grn':         { group: 'purchase',     resource: 'GoodsReceiptNote' },
  'purchase/orders':      { group: 'purchase',     resource: 'PurchaseOrder' },
  'purchase/invoices':    { group: 'purchase',     resource: 'PurchaseInvoice' },
  'purchase/requisitions':{ group: 'purchase',     resource: 'PurchaseRequisition' },
  'purchase/rfq':         { group: 'purchase',     resource: 'Rfq' },
  // Live API mounts Stock* entities under `purchase` group
  // (`/api/purchase/StockTransfer` returns 200, the `inventory`
  // variant 404s). Both URL slugs redirect to the `purchase` group.
  'purchase/transfer':    { group: 'purchase',     resource: 'StockTransfer' },
  'purchase/adjustment':  { group: 'purchase',     resource: 'StockAdjustment' },
  'purchase/balance':     { group: 'purchase',     resource: 'StockBalance' },
  'inventory/transfer':   { group: 'purchase',     resource: 'StockTransfer' },
  'inventory/adjustment': { group: 'purchase',     resource: 'StockAdjustment' },
  'inventory/balance':    { group: 'purchase',     resource: 'StockBalance' },
  'inventory/movement':   { group: 'inventory',    resource: 'InventoryMovement' },
  'mfg/quality':          { group: 'manufacturing', resource: 'QualityInspection' },
  'mfg/work-orders':      { group: 'manufacturing', resource: 'ProductionOrder' },
  'mfg/bom':              { group: 'manufacturing', resource: 'BillOfMaterial' },
  'mfg/work-centers':     { group: 'manufacturing', resource: 'WorkCenter' },
  'manufacturing/quality':{ group: 'manufacturing', resource: 'QualityInspection' },
  'accounting/coa':       { group: 'accounting',   resource: 'ChartOfAccount' },
  'accounting/jv':        { group: 'accounting',   resource: 'JournalEntry' },
  'accounting/recon':     { group: 'accounting',   resource: 'BankReconciliation' },
  'accounting/fiscal':    { group: 'accounting',   resource: 'AccountingPeriod' },
  'accounting/bank-accounts': { group: 'accounting', resource: 'BankAccount' },
  'accounting/budgets':   { group: 'accounting',   resource: 'Budget' },
  'accounting/tax-returns': { group: 'accounting', resource: 'TaxReturn' },
  // `hr/recruitment` truly has no live entity yet (G36 — backend gap).
  // Point to `Candidate` as the closest workable surface so the link
  // doesn't dead-end.
  'hr/recruitment':       { group: 'hrm',          resource: 'Candidate' },
  'hr/candidates':        { group: 'hrm',          resource: 'Candidate' },
  'hr/interviews':        { group: 'hrm',          resource: 'Interview' },
  'hr/offer-letters':     { group: 'hrm',          resource: 'OfferLetter' },
  'hr/attendance':        { group: 'hrm',          resource: 'Attendance' },
  'hr/leaves':            { group: 'hrm',          resource: 'LeaveApplication' },
  'hr/payroll':           { group: 'hrm',          resource: 'PayrollRun' },
  'hr/employees':         { group: 'hrm',          resource: 'Employee' },
  'hr/performance':       { group: 'hrm',          resource: 'PerformanceReview' },
  'hr/training':          { group: 'hrm',          resource: 'TrainingProgram' },
  // No dedicated SystemInfo entity — surface platform queue as a
  // proxy so the link doesn't dead-end.
  'system/info':          { group: 'platform',    resource: 'BackgroundQueueItem' },
  'system/health':        { group: 'platform',    resource: 'BackgroundQueueItem' },
  'system/diagnostics':   { group: 'platform',    resource: 'BackgroundQueueItem' },
  'system/maintenance':   { group: 'platform',    resource: 'BackgroundQueueItem' },
  'system/settings':      { group: 'platform',    resource: 'SystemSetting' },

  // ============================================================ //
  //  Single-segment slugs (used directly by `/panel/<slug>`)      //
  // ============================================================ //
  // ----- Sales / POS -----
  'sales-orders':       { group: 'sales',      resource: 'SalesOrder' },
  'sales-payments':     { group: 'sales',      resource: 'SalesPayment' },
  'invoices':           { group: 'sales',      resource: 'SalesInvoice' },
  'payments':           { group: 'sales',      resource: 'SalesPayment' },
  'credit-notes':       { group: 'sales',      resource: 'CreditNote' },
  'debit-notes':        { group: 'sales',      resource: 'DebitNote' },
  'delivery-challan':   { group: 'sales',      resource: 'DeliveryChallan' },
  'delivery-challans':  { group: 'sales',      resource: 'DeliveryChallan' },
  'pos-shifts':         { group: 'restaurant', resource: 'CashDrawerSession' },
  'cash-drawer':        { group: 'restaurant', resource: 'CashDrawerSession' },
  'pos-terminals':      { group: 'restaurant', resource: 'PosDevice' },

  // ----- Restaurant -----
  'menu-builder':       { group: 'restaurant', resource: 'MenuItem' },
  'menu-items':         { group: 'restaurant', resource: 'MenuItem' },
  'menu-categories':    { group: 'restaurant', resource: 'MenuCategory' },
  'kitchen-kot':        { group: 'restaurant', resource: 'KitchenOrderTicket' },
  'kots':               { group: 'restaurant', resource: 'KitchenOrderTicket' },
  'tables':             { group: 'restaurant', resource: 'RestaurantTable' },
  'reservations':       { group: 'restaurant', resource: 'TableReservation' },
  'home-delivery':      { group: 'restaurant', resource: 'DeliveryOrder' },
  'recipes':            { group: 'restaurant', resource: 'Recipe' },

  // ----- CRM -----
  'leads':              { group: 'crm', resource: 'Lead' },
  'opportunities':      { group: 'crm', resource: 'Opportunity' },
  'quotations':         { group: 'crm', resource: 'Quotation' },
  'campaigns':          { group: 'crm', resource: 'Campaign' },
  'support-tickets':    { group: 'crm', resource: 'SupportTicket' },
  // Loyalty isn't a "program" in the registry — closest is the per-customer account.
  'loyalty':            { group: 'crm', resource: 'CustomerLoyaltyAccount' },
  'loyalty-transactions': { group: 'crm', resource: 'LoyaltyTransaction' },

  // ----- Purchase -----
  'requisitions':       { group: 'purchase', resource: 'PurchaseRequisition' },
  'rfqs':               { group: 'purchase', resource: 'Rfq' },
  'rfq':                { group: 'purchase', resource: 'Rfq' },
  'purchase-orders':    { group: 'purchase', resource: 'PurchaseOrder' },
  // Live API uses "GoodsReceiptNote", not "GoodsReceipt".
  'goods-receipt':      { group: 'purchase', resource: 'GoodsReceiptNote' },
  'goods-receipts':     { group: 'purchase', resource: 'GoodsReceiptNote' },
  'purchase-invoices':  { group: 'purchase', resource: 'PurchaseInvoice' },

  // ----- Inventory -----
  'stock':              { group: 'inventory', resource: 'StockBalance' },
  'stock-balance':      { group: 'inventory', resource: 'StockBalance' },
  // Inventory movements live as "InventoryMovement", not "StockMovement".
  'stock-movements':    { group: 'inventory', resource: 'InventoryMovement' },
  'inventory-movements':{ group: 'inventory', resource: 'InventoryMovement' },
  'stock-adjustments':  { group: 'inventory', resource: 'StockAdjustment' },
  'stock-transfers':    { group: 'inventory', resource: 'StockTransfer' },
  'warehouses':         { group: 'master-data', resource: 'Warehouse' },

  // ----- Manufacturing -----
  'bom':                { group: 'manufacturing', resource: 'BillOfMaterial' },
  'boms':               { group: 'manufacturing', resource: 'BillOfMaterial' },
  'bill-of-materials':  { group: 'manufacturing', resource: 'BillOfMaterial' },
  // "Work orders" map onto the live API's `ProductionOrder` entity.
  'work-orders':        { group: 'manufacturing', resource: 'ProductionOrder' },
  'production-orders':  { group: 'manufacturing', resource: 'ProductionOrder' },
  'work-centers':       { group: 'manufacturing', resource: 'WorkCenter' },
  'quality-inspections':{ group: 'manufacturing', resource: 'QualityInspection' },

  // ----- Accounting -----
  'chart-of-accounts':  { group: 'accounting', resource: 'ChartOfAccount' },
  'accounts':           { group: 'accounting', resource: 'ChartOfAccount' },
  'journal-entries':    { group: 'accounting', resource: 'JournalEntry' },
  'ledger-entries':     { group: 'accounting', resource: 'LedgerEntry' },
  'bank-accounts':      { group: 'accounting', resource: 'BankAccount' },
  'bank-reconciliation':{ group: 'accounting', resource: 'BankReconciliation' },
  'bank-transactions':  { group: 'accounting', resource: 'BankStatementEntry' },
  'tax-returns':        { group: 'accounting', resource: 'TaxReturn' },
  'taxes':              { group: 'master-data', resource: 'TaxClass' },
  'tax':                { group: 'master-data', resource: 'TaxClass' },
  'tax-classes':        { group: 'master-data', resource: 'TaxClass' },
  // Live API mounts these under different names than the user-facing
  // slugs. The flat names 404 — see §31.
  'categories':         { group: 'master-data', resource: 'ProductCategory' },
  'category':           { group: 'master-data', resource: 'ProductCategory' },
  'product-categories': { group: 'master-data', resource: 'ProductCategory' },
  'units':              { group: 'master-data', resource: 'UnitOfMeasure' },
  'unit':               { group: 'master-data', resource: 'UnitOfMeasure' },
  'units-of-measure':   { group: 'master-data', resource: 'UnitOfMeasure' },
  'uom':                { group: 'master-data', resource: 'UnitOfMeasure' },
  // Brand is mounted at /master-data/ProductBrand on the live API.
  'brands':             { group: 'master-data', resource: 'ProductBrand' },
  'brand':              { group: 'master-data', resource: 'ProductBrand' },
  'product-brands':     { group: 'master-data', resource: 'ProductBrand' },
  'fiscal-year':        { group: 'accounting', resource: 'AccountingPeriod' },
  'budgets':            { group: 'accounting', resource: 'Budget' },

  // ----- HRMS -----
  'employees':          { group: 'hrm', resource: 'Employee' },
  'attendance':         { group: 'hrm', resource: 'Attendance' },
  // Live API: "LeaveApplication", not "LeaveRequest".
  'leave':              { group: 'hrm', resource: 'LeaveApplication' },
  'leaves':             { group: 'hrm', resource: 'LeaveApplication' },
  'leave-applications': { group: 'hrm', resource: 'LeaveApplication' },
  'leave-balance':      { group: 'hrm', resource: 'LeaveBalance' },
  'leave-types':        { group: 'hrm', resource: 'LeaveType' },
  // Live API: "HolidayCalendar", not "Holiday".
  'holidays':           { group: 'hrm', resource: 'HolidayCalendar' },
  'payroll':            { group: 'hrm', resource: 'PayrollRun' },
  'payslips':           { group: 'hrm', resource: 'Payslip' },
  'salary-components':  { group: 'hrm', resource: 'SalaryComponent' },
  'salary-structures':  { group: 'hrm', resource: 'SalaryStructure' },
  'performance':        { group: 'hrm', resource: 'PerformanceReview' },
  'candidates':         { group: 'hrm', resource: 'Candidate' },
  'interviews':         { group: 'hrm', resource: 'Interview' },
  'offer-letters':      { group: 'hrm', resource: 'OfferLetter' },
  // Live API splits training into Program + Enrollment — show the catalogue.
  'training':           { group: 'hrm', resource: 'TrainingProgram' },
  'training-programs':  { group: 'hrm', resource: 'TrainingProgram' },
  'training-enrollments':{ group: 'hrm', resource: 'TrainingEnrollment' },
  // Live API: "EmployeeFamilyMember".
  'family-member':      { group: 'hrm', resource: 'EmployeeFamilyMember' },
  'family-members':     { group: 'hrm', resource: 'EmployeeFamilyMember' },

  // ----- Asset management -----
  'fixed-assets':       { group: 'asset-management', resource: 'FixedAsset' },
  'depreciation':       { group: 'asset-management', resource: 'DepreciationSchedule' },

  // ----- Master data -----
  'price-lists':        { group: 'master-data', resource: 'PriceList' },
  'prices':             { group: 'master-data', resource: 'PriceList' },
  'pricing':            { group: 'master-data', resource: 'PriceList' },
  'geography':          { group: 'master-data', resource: 'Country' },
  'streams':            { group: 'master-data', resource: 'Stream' },
  'grades':             { group: 'master-data', resource: 'Grade' },
  // EmployeeFamily — live API name is `EmployeeFamilyMember`.
  'employee-family':    { group: 'hrm', resource: 'EmployeeFamilyMember' },
  'employeefamily':     { group: 'hrm', resource: 'EmployeeFamilyMember' },

  // ----- Organisation -----
  'companies':          { group: 'organization', resource: 'Company' },
  // OrganizationChart endpoint IS live (200 OK on bill tenant).
  'org-chart':          { group: 'organization', resource: 'OrganizationChart' },
  'organization-chart': { group: 'organization', resource: 'OrganizationChart' },
  'orgchart':           { group: 'organization', resource: 'OrganizationChart' },
  'goals':              { group: 'hrm', resource: 'PerformanceGoal' },
  'kpis':               { group: 'hrm', resource: 'KpiDefinition' },

  // ----- Admin / Identity -----
  'audit-log':          { group: 'audit',         resource: 'AuditLog' },
  'audit':              { group: 'audit',         resource: 'AuditLog' },
  'audit-trail':        { group: 'audit',         resource: 'AuditLog' },
  'webhooks':           { group: 'notifications', resource: 'WebhookSubscription' },
  'api-keys':           { group: 'identity',      resource: 'ApiKey' },
  'external-logins':    { group: 'identity',      resource: 'ExternalLogin' },
  // Maintenance ≈ scheduled platform jobs.
  'maintenance':        { group: 'platform',      resource: 'BackgroundQueueItem' },
  'migration':          { group: 'platform',      resource: 'BackgroundQueueItem' },

  // ----- Notifications -----
  'notification-centre':{ group: 'notifications', resource: 'Notification' },
  'notifications':      { group: 'notifications', resource: 'Notification' },
  'notification-templates': { group: 'notifications', resource: 'NotificationTemplate' },
  'notification-channels':  { group: 'notifications', resource: 'NotificationChannel' },
  'notification-preferences': { group: 'notifications', resource: 'NotificationPreference' },
  'publish-test-event': { group: 'notifications', resource: 'Notification' },
  'publish':            { group: 'notifications', resource: 'Notification' },
  'publish-event':      { group: 'notifications', resource: 'Notification' },
  // ---- Analytics (no dedicated entity yet — point at the dashboards) ----
  'analytics':          { group: 'reporting',     resource: 'ReportDefinition' },
  'dashboards':         { group: 'reporting',     resource: 'ReportDefinition' },
  'reports':            { group: 'reporting',     resource: 'ReportDefinition' },
  'headcount-trend':    { group: 'reporting',     resource: 'ReportDefinition' },

  // ----- Tenant -----
  'tenant-info':        { group: 'tenancy', resource: 'Tenant' },
  'billing':            { group: 'tenancy', resource: 'BillingAccount' },
  'invitations':        { group: 'tenancy', resource: 'TenantInvitation' },

  // ----- System -----
  'system-info':        { group: 'platform', resource: 'BackgroundQueueItem' },
  'diagnostics':        { group: 'platform', resource: 'BackgroundQueueItem' },
  'health-check':       { group: 'platform', resource: 'BackgroundQueueItem' },
  'settings':           { group: 'platform', resource: 'SystemSetting' },
  'system-settings':    { group: 'platform', resource: 'SystemSetting' },
  // No dedicated License entity in registry yet — show subscription plans.
  'license':            { group: 'tenancy',  resource: 'Tenant' },
};

/* -------------------------------------------------------------------- */
/*  Resolver                                                            */
/* -------------------------------------------------------------------- */

function findResource(name: string): ApiResourceMeta | null {
  // Case-insensitive exact match on `name`.
  const lower = name.toLowerCase();
  return apiResources.find((r) => r.name.toLowerCase() === lower) ?? null;
}

/** Case-insensitive SLUG_MAP accessor. */
function slugMapLookup(slug: string): SlugTarget | null {
  const direct = SLUG_MAP[slug];
  if (direct) return direct;
  const lower = slug.toLowerCase();
  // Fallback: scan keys case-insensitively (handles `Units`, `Categories`,
  // `OrganizationChart` etc. from URL fragments that preserve the menu's
  // PascalCase).
  for (const key of Object.keys(SLUG_MAP)) {
    if (key.toLowerCase() === lower) return SLUG_MAP[key]!;
  }
  return null;
}

function autoMatch(slug: string): ApiResourceMeta | null {
  // sales-orders → SalesOrders → SalesOrder
  const pascal = slug
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const candidates = [pascal, pascal.replace(/ies$/, 'y'), pascal.replace(/s$/, '')];
  for (const c of candidates) {
    const hit = findResource(c);
    if (hit) return hit;
  }
  return null;
}

export function resolveSlug(slug: string): ApiResourceMeta | null {
  if (!slug) return null;

  // Try the full path (case-insensitive).
  const direct = slugMapLookup(slug);
  if (direct) {
    const hit = findResource(direct.resource);
    if (hit) return hit;
  }
  const auto = autoMatch(slug);
  if (auto) return auto;

  // Live `/api/Menu/all` returns nested URLs like
  // `sales-billing/sales-orders` or `organization/OrganizationChart`.
  // The leaf segment is the most specific — try each segment from
  // leaf → root before giving up.
  const segments = slug.split('/').filter(Boolean);
  if (segments.length > 1) {
    for (let i = segments.length - 1; i >= 0; i--) {
      const seg = segments[i];
      // 1) Case-insensitive slug-map hit
      const t = slugMapLookup(seg);
      if (t) {
        const hit = findResource(t.resource);
        if (hit) return hit;
      }
      // 2) Direct entity-name match (handles `OrganizationChart`,
      // `EmployeeFamilyMember` straight from the URL).
      const direct2 = findResource(seg);
      if (direct2) return direct2;
      // 3) PascalCase auto-match
      const a = autoMatch(seg);
      if (a) return a;
    }
  }
  return null;
}

/* -------------------------------------------------------------------- */
/*  Component                                                           */
/* -------------------------------------------------------------------- */

interface Props {
  /** When omitted, the slug is taken from the current URL's first segment under /panel/. */
  slug?: string;
}

export default function ModuleRedirect({ slug }: Props) {
  const params = useParams();
  const loc = useLocation();
  const fromUrl = params['*'] ?? loc.pathname.replace(/^\/panel\/?/, '').split('/')[0] ?? '';
  const effective = (slug ?? fromUrl).trim();
  const res = resolveSlug(effective);

  if (res) {
    return <Navigate to={`/panel/api/${res.group}/${res.name}`} replace />;
  }

  return <UnknownModuleFallback slug={effective} />;
}

/**
 * Friendlier "we couldn't find that module" page. Better than the
 * old static "Coming Soon" because it actually helps the user.
 */
function UnknownModuleFallback({ slug }: { slug: string }) {
  // Fuzzy: list resources whose name shares any token with the slug.
  const tokens = slug.toLowerCase().split(/[-_/\s]+/).filter(Boolean);
  const suggestions = apiResources
    .filter((r) => {
      const lc = r.name.toLowerCase();
      return tokens.some((t) => t.length >= 3 && lc.includes(t));
    })
    .slice(0, 8);

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <Alert variant="info">
        <strong>Module &ldquo;{slug || 'unknown'}&rdquo; isn&rsquo;t mapped yet.</strong>{' '}
        We can still let you browse every entity through the auto-generated
        API browser below.
      </Alert>

      <Card>
        <div className="flex items-start gap-3 p-4">
          <Database className="mt-1 text-primary" size={20} />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-content-primary">Open the API browser</h2>
            <p className="text-xs text-content-muted">
              Every entity defined in the live swagger has a working list + edit + delete UI.
              Pick a group or jump straight into a resource.
            </p>
          </div>
          <Link to="/panel/api">
            <Button size="sm">
              All modules <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </Card>

      {suggestions.length > 0 && (
        <Card>
          <div className="p-4">
            <h3 className="mb-2 text-sm font-semibold text-content-primary">
              Did you mean one of these?
            </h3>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {suggestions.map((s) => (
                <li key={s.name}>
                  <Link
                    to={`/panel/api/${s.group}/${s.name}`}
                    className="inline-flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-xs hover:border-primary"
                  >
                    <span>
                      <span className="font-medium text-content-primary">{s.name}</span>
                      <span className="ml-2 text-content-muted">· {s.group}</span>
                    </span>
                    <ExternalLink size={12} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}
